import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { FAQS } from "../../data/homeData";

// FAQ section — doubles as schema.org FAQPage structured data for Google rich results
// and direct-quotable answers for AI engines (ChatGPT, Perplexity, Google AI Overviews).
const FAQ = () => {
  const [open, setOpen] = useState(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section id="faq" data-testid="faq-section" className="py-16 md:py-20 bg-[var(--ac-cream)]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="ac-container max-w-3xl">
        <div className="text-center mb-10">
          <div className="eyebrow">Questions &amp; Answers</div>
          <h2 className="section-heading mt-3" data-testid="faq-heading">
            Frequently Asked Questions
          </h2>
        </div>

        <div
          className="rounded-xl border border-[var(--ac-line)] overflow-hidden bg-white shadow-[0_24px_50px_-36px_rgba(29,66,108,0.28)]"
          data-testid="faq-accordion"
        >
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div
                key={f.q}
                className={`border-b border-[var(--ac-line)] last:border-b-0 ${isOpen ? "bg-[var(--ac-cream)]" : "bg-white"}`}
                data-testid={`faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex items-center gap-3 md:gap-4 text-left px-4 md:px-6 py-4 hover:bg-[var(--ac-cream)] transition"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="flex-1 text-[15px] md:text-[17px] font-bold text-[var(--ac-ink)] leading-snug">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-[var(--ac-royal)] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {/*
                  2026-10 SEO fix — every answer is ALWAYS rendered to the
                  HTML so Googlebot (and the FAQPage schema) sees the full
                  text whether the accordion item is open or not. The
                  `hidden` attribute hides collapsed items visually and
                  from the assistive-tech tree WITHOUT dropping them
                  from the DOM. Previously only the open answer was
                  mounted, so bots indexed only the first Q+A pair.
                */}
                <div
                  id={`faq-answer-${i}`}
                  role="region"
                  aria-labelledby={`faq-toggle-${i}`}
                  hidden={!isOpen}
                  className={`px-4 md:px-6 pb-5 ${isOpen ? "ac-fade-up" : ""}`}
                  data-testid={`faq-answer-${i}`}
                >
                  <p className="text-[var(--ac-ink)] text-[14.5px] md:text-[15px] leading-relaxed">
                    {f.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
